import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { throwError as observableThrowError, Observable, from } from "rxjs";
import { catchError } from "rxjs/operators";
import { OrderEntity } from "../../models/entity/order/order.entity";
import { OrderItemEntity } from "../../models/entity/order/order-item.entity";
import { CampaignEntity } from "../../models/entity/campaign/campaign.entity";
import { OrderWithItemsDto } from "../../models/dto/orderWithItemsDto";
import { OrderItemWithDetailDto } from "../../models/dto/orderItemWithDetailDto";
import { PaymentDetailView } from "../../models/dto/paymentDetailView";
import { OrderProcessRequest } from "../../models/rest/order/orderProcessRequest";
import { OrderProcessResponse } from "../../models/rest/order/orderProcessResponse";
import { ReceiptLabelResponse } from "../../models/rest/setting/receiptLabelResponse";
import { PaymentCreateBulkRequest } from "../../models/rest/payment/paymentCreateBulkRequest";
import { PaymentCreateBulkResult } from "../../models/rest/payment/paymentCreateBulkResult";
import { OrderHangerRefundView } from "../../models/dto/order/order-hanger-refund-view.model";
import { OrderDeleteRequest } from "../../models/rest/order/order-delete-request.model";
import { OrderApplyCampaignRequest } from "../../models/rest/order/order-apply-campaign-request.model";
import { OrderListHtmlRequest } from "../../models/rest/order/order-list-html-request";
import { BaseRestService } from "../base-rest.service";
import { SessionStorageService } from "../session-storage.service";
import { OnlineCollectionUrlCreateResponse } from "../../models/rest/online-collection/online-collection-url-create-response.model";
import { OrderChangeCurrencyRateRequest } from "../../models/rest/order/order-change-currency-request.model";
import { ExportFileResponse } from "../../models/rest/common/export-file-response";
import { OrderItemNoteReceiptRequest } from "../../models/rest/order/order-item-note-receipt-request";
import { StringUtils } from '../../utils/string.utils';
import { OrderScreenRequest } from "../../models/rest/order/order-screen-request";
import { OrderScreenResponse } from "../../models/rest/order/order-screen-response";
import { OrderLinkRequest } from "../../models/rest/order/order-link-request";
import { OrderLinkResponse } from '../../models/rest/order/order-link-response';
import { OrderAddDiscountAmountRequest } from '../../models/rest/order/order-add-discount-amount-request';

@Injectable({
  providedIn: "root"
})
export class OrderService extends BaseRestService {
  constructor(protected httpClient: HttpClient, protected sessionStorageService: SessionStorageService) {
    super(httpClient, sessionStorageService);
  }

  //sipariş bilgisi
  getOrderById(orderId: number): Observable<OrderEntity> {
    const url = `/orders/${orderId}`;
    return this.httpGet<OrderEntity>(url).pipe(catchError(this.errorHandler));
  }

  getOrderScreen(request: OrderScreenRequest): Observable<OrderScreenResponse> {
    const url = `/orders/screen`;
    return this.httpPost<OrderScreenResponse>(url, request).pipe(catchError(this.errorHandler));
  }

  setOrderUncompleted(orderId: number) {
    const url = `/orders/${orderId}/set-uncompleted`;
    return this.httpGet(url).pipe(catchError(this.errorHandler));
  }


  //sipariş bilgisi
  getOrderByOrderCode(orderCode: string): Observable<OrderEntity> {
    if (StringUtils.isEmpty(orderCode))
      observableThrowError("Sipariş numarası boş olamaz.");

    const url = `/orders?order-code=${orderCode}`;
    return this.httpGet<OrderEntity>(url).pipe(catchError(this.errorHandler));
  }

  createOrMofifyOrder(request: OrderWithItemsDto): Observable<OrderWithItemsDto> {


    let url;
    if (request.order?.id && request.order.id > 0) url = `/orders/modify`;
    else
      url = `/orders/create`;


    return this.httpPost<OrderWithItemsDto>(url, request)
      .pipe(catchError(this.errorHandler));
  }

  //sipariş entity güncelle
  updateOrder(id, order): Observable<OrderEntity> {
    const url = `/orders/${id}`;
    return this.httpPut<OrderEntity>(url, order).pipe(catchError(this.errorHandler));
  }
  //sipariş entity güncelle
  changeOrderDate(data): Observable<OrderEntity> {
    const url = `/orders/change-order-date`;
    return this.httpPost(url, data).pipe(catchError(this.errorHandler));
  }
  //sipariş bilgisi
  getOrderWithItems(orderId: number): Observable<OrderWithItemsDto> {
    const url = `/orders/${orderId}/order-with-item`;
    return this.httpGet<OrderWithItemsDto>(url)
      .pipe(catchError(this.errorHandler));
  }

  //siparişe ait ürün ve işlem tipleri
  getOrderItemDetails(orderId: number): Observable<OrderItemWithDetailDto[]> {
    const url = `/orders/${orderId}/order-item-detail`;
    return this.httpGet<OrderItemWithDetailDto[]>(url)
      .pipe(catchError(this.errorHandler));
  }

  //siparişe ait ödemeler
  getPaymentDetails(orderId: number): Observable<PaymentDetailView[]> {
    const url = `/orders/${orderId}/payment-details`;
    return this.httpGet<PaymentDetailView[]>(url)
      .pipe(catchError(this.errorHandler));
  }

  //sipariş entity güncelle
  processOrderItem(
    orderItemIds: number[],
    processType: string
  ): Observable<OrderItemEntity[]> {
    const url = `/order-items/process/process-type=${processType}`;
    return this.httpPut<OrderItemEntity[]>(url, orderItemIds)
      .pipe(catchError(this.errorHandler));
  }

  processOrderBulk(
    request: OrderProcessRequest
  ): Observable<OrderProcessResponse[]> {
    const url = `/orders/process`;

    return this.httpPost<OrderProcessResponse[]>(url, request)
      .pipe(catchError(this.errorHandler));
  }

  //sipariş için sms gönderimi
  sendOrderSms(id: number, messageType: string) {

    const url = `/orders/${id}/send-sms?message-type=${messageType}`;
    return this.httpGet(url).pipe(catchError(this.errorHandler));
  }

  //sipariş için sms gönderimi
  sendOrderEmail(id: number) {
    const url = `/orders/${id}/send-email`;
    return this.httpGet(url).pipe(catchError(this.errorHandler));
  }
  //sipariş için fiş html i al
  getReceiptHtml(id: number): Observable<ReceiptLabelResponse> {
    const url = `/orders/${id}/receipt-html`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }

  getReceipt(id: number): Observable<ReceiptLabelResponse> {
    const url = `/orders/${id}/receipt`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }

  getReceiptPdf(id: number): Observable<ReceiptLabelResponse> {
    const url = `/orders/${id}/receipt-pdf`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }
  //sipariş için fiş html i al
  getAdditionalReceiptHtml(id: number): Observable<ReceiptLabelResponse> {
    const url = `/orders/${id}/additional-receipt-html`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }
  //sipariş için fiş html i al
  getLabel(id: number): Observable<ReceiptLabelResponse> {
    const url = `/orders/${id}/label`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }
  getOrderListHtml(data: OrderListHtmlRequest): Observable<ReceiptLabelResponse> {
    const url = `/orders/order-list-html`;
    return this.httpPost<ReceiptLabelResponse>(url, data)
      .pipe(catchError(this.errorHandler));
  }
  getOpenCashDrawer(): Observable<ReceiptLabelResponse> {
    const url = `/orders/open-cash-drawer`;
    return this.httpGet<ReceiptLabelResponse>(url)
      .pipe(catchError(this.errorHandler));
  }
  createBulkPayment(payment: PaymentCreateBulkRequest): Observable<PaymentCreateBulkResult[]> {
    const url = `/orders/bulk-payment`;
    return this.httpPost<PaymentCreateBulkResult[]>(url, payment).pipe(catchError(this.errorHandler));
  }

  //siparişe ait log kayıtları
  getAvailableCampaigns(orderId: number): Observable<CampaignEntity[]> {
    const url = `/orders/${orderId}/available-campaigns`;
    return this.httpGet<CampaignEntity[]>(url)
      .pipe(catchError(this.errorHandler));
  }

  //siparişi sil
  deleteOrder(data: OrderDeleteRequest) {
    const url = `/orders/delete`;
    return this.httpPost(url, data).pipe(catchError(this.errorHandler));
  }

  calculateFinalPrice(data: OrderEntity): Observable<OrderEntity> {
    const url = `/orders/calculate-final-price`;
    return this.httpPost<OrderEntity>(url, data).pipe(catchError(this.errorHandler));
  }
  calculateFinalPriceWithSave(data: OrderEntity): Observable<OrderEntity> {
    const url = `/orders/calculate-final-price-with-save`;
    return this.httpPost<OrderEntity>(url, data).pipe(catchError(this.errorHandler));
  }

  //siparişe ait log kayıtları
  getOrderHangerRefunds(orderId: number): Observable<OrderHangerRefundView[]> {
    const url = `/orders/${orderId}/hanger-refunds`;
    return this.httpGet<OrderHangerRefundView[]>(url)
      .pipe(catchError(this.errorHandler));
  }
  calculateStorageCostByOrderId(orderId: number) {
    const url = `/orders/${orderId}/calculate-storage-cost`;
    return this.httpGet(url)
      .pipe(catchError(this.errorHandler));
  }

  applyCampaign(request: OrderApplyCampaignRequest): Observable<OrderEntity> {
    const url = `/orders/apply-campaign`;
    return this.httpPost(url, request)
      .pipe(catchError(this.errorHandler));
  }

  applyCampaignReturnWithItems(request: OrderApplyCampaignRequest): Observable<OrderWithItemsDto> {
    const url = `/orders/apply-campaign-return-with-items`;
    return this.httpGet<OrderWithItemsDto>(url)
      .pipe(catchError(this.errorHandler));
  }
  //siparişe ait log kayıtları
  createPaymentUrl(orderId: number): Observable<OnlineCollectionUrlCreateResponse> {
    const url = `/orders/${orderId}/create-oayment-url`;
    return this.httpGet<OnlineCollectionUrlCreateResponse>(url)
      .pipe(catchError(this.errorHandler));
  }

  getOrderDebt(orderId: number): Observable<number> {
    const url = `/orders/${orderId}/order-debt`;
    return this.httpGet<number>(url)
      .pipe(catchError(this.errorHandler));
  }

  addDiscountAmount(request: OrderAddDiscountAmountRequest): Observable<any> {
    const url = `/orders/add-discount`;
    return this.httpPost(url, request).pipe(catchError(this.errorHandler));
  }

  getPaidAmount(orderId: number): Observable<number> {
    const url = `/orders/${orderId}/paid-amount`;
    return this.httpGet<number>(url)
      .pipe(catchError(this.errorHandler));
  }


  changeCurrencyRate(request: OrderChangeCurrencyRateRequest): Observable<OrderEntity> {
    const url = `/orders/change-currency`;
    return this.httpPost(url, request)
      .pipe(catchError(this.errorHandler));
  }

  getOrderItemNoteReceipt(request: OrderItemNoteReceiptRequest): Observable<ExportFileResponse> {
    const url = `/orders/order-item-note-receipt`;
    return this.httpPost(url, request)
      .pipe(catchError(this.errorHandler));
  }

  // Son siparişi getir
  getLastOrder(storeId: number): Observable<OrderEntity> {
    const url = `/orders/last-order-by-store-id?store-id=${storeId}`;
    return this.httpGet<OrderEntity>(url)
      .pipe(catchError(this.errorHandler));
  }


  getOrderLink(id: number, request: OrderLinkRequest): Observable<OrderLinkResponse> {
    const url = `/orders/${id}/order-link`;
    return this.httpPost<OrderLinkResponse>(url, request)
      .pipe(catchError(this.errorHandler));
  }

  errorHandler(error: HttpErrorResponse) {
    //console.log(error);
    return observableThrowError(error);
  }
}
